import * as React from 'react';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import { getEmployers, getBrokers, createEmployer, createBroker, renameEmployer, renameBroker } from './../../api/api';
import { 
    styles,
    CreatableComboBoxContainer,
} from './CreatableComboBox.styles';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';
import { useToast } from '../ToastProvider/ToastProvider';

const filter = createFilterOptions();

export default function CreatableComboBox({ onEmployerChange, onBrokerChange }) {
  const [employerValue, setEmployerValue] = React.useState(null);
  const [brokerValue, setBrokerValue] = React.useState(null);
  const [open, toggleOpen] = React.useState(false);
  const [dialogType, setDialogType] = React.useState('');
  const [employerOptions, setEmployerOptions] = React.useState([]);
  const [brokerOptions, setBrokerOptions] = React.useState([]);
  const { showToast } = useToast();

  const [renameDialogOpen, setRenameDialogOpen] = React.useState(false);
  const [renameType, setRenameType] = React.useState('');
  const [renameValue, setRenameValue] = React.useState('');
  const [renameId, setRenameId] = React.useState(null);
  const [renaming, setRenaming] = React.useState(false);
  
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const employers = await getEmployers();
        setEmployerOptions(employers.map((employer) => ({ title: employer.name, id: employer.id })));
        const brokers = await getBrokers();
        setBrokerOptions(brokers.map((broker) => ({ title: broker.name, id: broker.id })));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  React.useEffect(() => {
    if (employerValue) {
      onEmployerChange(employerValue);
    }
  }, [employerValue]);

  React.useEffect(() => {
    if (brokerValue) {
      onBrokerChange(brokerValue);
    }
  }, [brokerValue]);

  const handleClose = () => {
    setDialogValue({ title: '' });
    toggleOpen(false);
  };

  const [dialogValue, setDialogValue] = React.useState({ title: '' });

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    try {
        if (dialogType === 'employer') {
            const newEmployer = await createEmployer(dialogValue.title);
            setEmployerOptions((prev) => [...prev, { title: newEmployer.name, id: newEmployer.id }]);
            setEmployerValue({ title: newEmployer.name, id: newEmployer.id });
        } else if (dialogType === 'broker') {
            const newBroker = await createBroker(dialogValue.title);
            setBrokerOptions((prev) => [...prev, { title: newBroker.name, id: newBroker.id }]);
            setBrokerValue({ title: newBroker.name, id: newBroker.id });
        }
    } catch (error) {
        console.error("Error adding new entity:", error);
    }

    handleClose();
};

  const handleRenameClick = (type, value) => {
    setRenameType(type);
    setRenameValue(value.title);
    setRenameId(value.id);
    setRenameDialogOpen(true);
  };

  const handleRenameDialogClose = () => {
    setRenameDialogOpen(false);
    setRenameValue('');
    setRenameId(null);
    setRenameType('');
  };

  const handleRenameSubmit = async (event) => {
    event.preventDefault();
    setRenaming(true);
    try {
      if (renameType === 'employer') {
        await renameEmployer(renameId, renameValue);
        setEmployerOptions((prev) =>
          prev.map((opt) =>
            opt.id === renameId ? { ...opt, title: renameValue } : opt
          )
        );
        if (employerValue && employerValue.id === renameId) {
          setEmployerValue({ ...employerValue, title: renameValue });
        }
      } else if (renameType === 'broker') {
        await renameBroker(renameId, renameValue);
        setBrokerOptions((prev) =>
          prev.map((opt) =>
            opt.id === renameId ? { ...opt, title: renameValue } : opt
          )
        );
        if (brokerValue && brokerValue.id === renameId) {
          setBrokerValue({ ...brokerValue, title: renameValue });
        }
      }
      handleRenameDialogClose();
    } catch (error) {
      if (error.isAuthExpired) return;
      showToast(
        `Renaming the ${renameType === 'employer' ? 'employer' : 'broker'} failed. Please try again.`,
        'error'
      );
    } finally {
      setRenaming(false);
    }
  };

  return (
    <CreatableComboBoxContainer>
      <Stack direction="row" spacing={2}>
        <Autocomplete
          value={employerValue}
          onChange={(event, newValue) => {
            if (typeof newValue === 'string') {
              setTimeout(() => {
                toggleOpen(true);
                setDialogType('employer');
                setDialogValue({ title: newValue });
              });
            } else if (newValue && newValue.inputValue) {
              toggleOpen(true);
              setDialogType('employer');
              setDialogValue({ title: newValue.inputValue });
            } else {
              setEmployerValue(newValue);
            }
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            if (params.inputValue !== '') {
              filtered.push({
                inputValue: params.inputValue,
                title: `Add "${params.inputValue}"`,
              });
            }
            return filtered;
          }}
          data-testid="employer-combobox"
          options={employerOptions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
          getOptionKey={(option) => option.id ?? option.inputValue}
          selectOnFocus
          handleHomeEndKeys
          renderInput={(params) => <TextField {...params} label="Employer" />}
          sx={styles.autocomplete}
          freeSolo
          renderOption={({ key, ...props }, option) => (
            <li key={key} {...props}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                <span>{option.title}</span>
                {!option.inputValue && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRenameClick('employer', option);
                    }}
                    sx={{
                      color: 'grey.500',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'grey.100',
                      },
                    }}
                    data-testid="employer-rename-option-button"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </li>
          )}
        />
        <Autocomplete
          value={brokerValue}
          onChange={(event, newValue) => {
            if (typeof newValue === 'string') {
              setTimeout(() => {
                toggleOpen(true);
                setDialogType('broker');
                setDialogValue({ title: newValue });
              });
            } else if (newValue && newValue.inputValue) {
              toggleOpen(true);
              setDialogType('broker');
              setDialogValue({ title: newValue.inputValue });
            } else {
              setBrokerValue(newValue);
            }
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            if (params.inputValue !== '') {
              filtered.push({
                inputValue: params.inputValue,
                title: `Add "${params.inputValue}"`,
              });
            }
            return filtered;
          }}
          data-testid="broker-combobox"
          options={brokerOptions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
          getOptionKey={(option) => option.id ?? option.inputValue}
          selectOnFocus
          handleHomeEndKeys
          renderInput={(params) => <TextField {...params} label="Broker" />}
          sx={styles.autocomplete}
          freeSolo
          renderOption={({ key, ...props }, option) => (
            <li key={key} {...props}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                <span>{option.title}</span>
                {!option.inputValue && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRenameClick('broker', option);
                    }}
                    sx={{
                      color: 'grey.500',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'grey.100',
                      },
                    }}
                    data-testid="broker-rename-option-button"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </li>
          )}
        />
      </Stack>
      <Dialog open={open} onClose={handleClose}>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Add a new {dialogType === 'employer' ? 'Employer' : 'Broker'}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please, add {dialogType === 'employer' ? 'employer' : 'broker'}
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              value={dialogValue.title}
              onChange={(event) =>
                setDialogValue({
                  ...dialogValue,
                  title: event.target.value,
                })
              }
              label="Title"
              type="text"
              variant="standard"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button data-testid="submit-button" type="submit">Add</Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={renameDialogOpen} onClose={handleRenameDialogClose}>
        <form onSubmit={handleRenameSubmit}>
          <DialogTitle>Rename {renameType === 'employer' ? 'Employer' : 'Broker'}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter a new name for the {renameType === 'employer' ? 'employer' : 'broker'}.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="rename"
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              label="New Name"
              type="text"
              variant="standard"
              disabled={renaming}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRenameDialogClose} disabled={renaming}>Cancel</Button>
            <Button type="submit" disabled={renaming || !renameValue.trim()}>
              {renaming ? <CircularProgress size={18} /> : 'Rename'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </CreatableComboBoxContainer>
  );
}